import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const NotificationContext = createContext(null);

const AUTO_DISMISS_MS = 4000;
let nextToastId = 1;

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const location = useLocation();

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((type, message) => {
    const id = nextToastId++;
    setToasts((current) => [...current, { id, type, message }]);
    setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  }, [dismiss]);

  const success = useCallback((message) => push('success', message), [push]);
  const error = useCallback((message) => push('error', message), [push]);
  const warning = useCallback((message) => push('warning', message), [push]);

  // `notify` must keep a stable identity: consumers list it in useEffect and
  // useCallback dependency arrays, so rebuilding the object on every provider
  // render would re-trigger their data loads every time a toast appears.
  const notify = useMemo(
    () => ({ success, error, warning }),
    [success, error, warning]
  );

  useEffect(() => {
    setToasts([]);
  }, [location.pathname]);

  const value = useMemo(
    () => ({ toasts, notify, dismiss }),
    [toasts, notify, dismiss]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
